import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { adminApi, ApiError } from '../lib/api';
import { useOperatorPermissions } from '../hooks/useOperatorPermissions';
import { useToast } from '../context/ToastContext';
import { copy, formatModelName, translateApiError } from '../lib/copy';
import { cn, formatDate } from '../lib/utils';
import { ErrorState, LoadingState, PageHeader, StatusBadge } from '../components/ui';
import { useProductScope } from '../context/ProductScopeContext';

type Tab = 'overview' | 'memory';

const PROMPT_PREVIEW = 500;

export function AgentDetailPage() {
  const { id = '' } = useParams();
  const scope = useProductScope();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'memory' ? 'memory' : 'overview';
  const [tab, setTab] = useState<Tab>(initialTab);
  const [showPrompt, setShowPrompt] = useState(false);
  const [status, setStatus] = useState('');
  const [model, setModel] = useState('');
  const queryClient = useQueryClient();
  const { pushToast } = useToast();
  const { canManageAgents, permissionDeniedHint } = useOperatorPermissions();

  const agentQuery = useQuery({
    queryKey: ['agents', id],
    queryFn: () => adminApi.agent(id),
    enabled: Boolean(id),
  });

  const memoriesQuery = useQuery({
    queryKey: ['agents', id, 'memories'],
    queryFn: () => adminApi.agentMemories(id),
    enabled: Boolean(id) && tab === 'memory',
  });

  const patchMutation = useMutation({
    mutationFn: (payload: { status?: string; model?: string }) =>
      adminApi.patchAgent(id, payload),
    onSuccess: () => {
      pushToast(copy.agentDetail.agentSaved, 'success');
      queryClient.invalidateQueries({ queryKey: ['agents', id] });
    },
    onError: (err: unknown) => {
      pushToast(
        err instanceof ApiError ? translateApiError(err.message) : copy.errors.updateAgent,
        'error',
      );
    },
  });

  const deleteMemoryMutation = useMutation({
    mutationFn: (memoryId: string) => adminApi.deleteAgentMemory(id, memoryId),
    onSuccess: () => {
      pushToast(copy.agentDetail.memoryDeleted, 'success');
      queryClient.invalidateQueries({ queryKey: ['agents', id, 'memories'] });
      queryClient.invalidateQueries({ queryKey: ['agents', id] });
    },
    onError: () => pushToast(copy.errors.deleteMemory, 'error'),
  });

  const handlePatch = (e: FormEvent) => {
    e.preventDefault();
    if (!canManageAgents) return;
    patchMutation.mutate({
      status: status.trim() || undefined,
      model: model.trim() || undefined,
    });
  };

  if (agentQuery.isLoading) return <LoadingState />;
  if (agentQuery.isError || !agentQuery.data) {
    return <ErrorState message={copy.errors.loadAgent} onRetry={() => agentQuery.refetch()} />;
  }

  const agent = agentQuery.data;
  const promptPreview =
    agent.system_prompt.length > PROMPT_PREVIEW && !showPrompt
      ? `${agent.system_prompt.slice(0, PROMPT_PREVIEW)}...`
      : agent.system_prompt;

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: copy.agentDetail.overview },
    { id: 'memory', label: copy.agentDetail.memory },
  ];

  return (
    <div>
      <Link to={`/${scope}/agents`} className="mb-4 inline-flex items-center gap-1 text-sm text-ops-muted hover:text-ops-text">
        <ArrowLeft className="h-4 w-4" />
        {copy.agentDetail.back}
      </Link>
      <PageHeader
        title={agent.name}
        description={`${copy.agentDetail.owner}: ${agent.user.firstName}`}
        actions={
          <Link to={`/${scope}/users/${agent.user.id}`} className="ops-btn-secondary">
            {agent.user.firstName}
          </Link>
        }
      />

      <div className="mb-4 flex gap-2 border-b border-ops-border">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={cn('ops-tab', tab === item.id ? 'ops-tab--active' : 'ops-tab--inactive')}
          >
            {item.label}
            {item.id === 'memory' ? ` (${agent.memories_count})` : ''}
          </button>
        ))}
      </div>

      {tab === 'overview' ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="ops-card space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-ops-muted">{copy.agentDetail.status}</span>
              <StatusBadge status={agent.status} kind="agent" />
            </div>
            <div className="flex justify-between">
              <span className="text-ops-muted">{copy.agentDetail.model}</span>
              <span>{formatModelName(agent.model)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ops-muted">{copy.agentDetail.temperature}</span>
              <span>{agent.temperature}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ops-muted">{copy.agentDetail.messages}</span>
              <span>{agent.messages_count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ops-muted">{copy.agentDetail.sources}</span>
              <span>{agent.sources_count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ops-muted">{copy.agentDetail.memories}</span>
              <span>{agent.memories_count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ops-muted">{copy.agentDetail.memoryEnabled}</span>
              <span>{agent.memory_enabled ? copy.common.yes : copy.common.no}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ops-muted">{copy.agentDetail.created}</span>
              <span>{formatDate(agent.created_at)}</span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="ops-card">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-sm font-semibold">{copy.agentDetail.systemPrompt}</h2>
                {agent.system_prompt.length > PROMPT_PREVIEW ? (
                  <button
                    type="button"
                    className="text-xs text-ops-muted hover:text-ops-text"
                    onClick={() => setShowPrompt((v) => !v)}
                  >
                    {showPrompt ? copy.agentDetail.hidePrompt : copy.agentDetail.showPrompt}
                  </button>
                ) : null}
              </div>
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap text-xs text-ops-secondary">
                {promptPreview || copy.common.notAvailable}
              </pre>
            </div>

            {canManageAgents ? (
              <div className="ops-card">
                <h2 className="mb-3 text-sm font-semibold">{copy.agentDetail.editAgent}</h2>
                <form onSubmit={handlePatch} className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs text-ops-muted">{copy.agentDetail.status}</label>
                    <input
                      className="ops-input"
                      placeholder={agent.status}
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-ops-muted">{copy.agentDetail.model}</label>
                    <input
                      className="ops-input"
                      placeholder={agent.model ?? ''}
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                    />
                  </div>
                  <button type="submit" className="ops-btn-primary" disabled={patchMutation.isPending}>
                    {copy.agentDetail.saveAgent}
                  </button>
                </form>
              </div>
            ) : (
              <p className="text-sm text-ops-muted">{permissionDeniedHint}</p>
            )}
          </div>
        </div>
      ) : null}

      {tab === 'memory' ? (
        <div className="ops-card overflow-hidden p-0">
          {memoriesQuery.isLoading ? <LoadingState /> : null}
          {memoriesQuery.data?.items.length === 0 ? (
            <p className="p-4 text-sm text-ops-muted">{copy.agentDetail.memoryEmpty}</p>
          ) : null}
          {memoriesQuery.data && memoriesQuery.data.items.length > 0 ? (
            <table className="ops-table">
              <thead>
                <tr>
                  <th>{copy.agentDetail.fact}</th>
                  <th>{copy.agentDetail.source}</th>
                  <th>{copy.agentDetail.created}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {memoriesQuery.data.items.map((row) => (
                  <tr key={row.id}>
                    <td className="max-w-md">{row.fact}</td>
                    <td>{row.source}</td>
                    <td className="text-ops-muted">{formatDate(row.created_at)}</td>
                    <td>
                      {canManageAgents ? (
                        <button
                          type="button"
                          className="ops-btn-danger py-1 text-xs"
                          disabled={deleteMemoryMutation.isPending}
                          onClick={() => {
                            if (window.confirm(copy.agentDetail.memoryDeleteConfirm)) {
                              deleteMemoryMutation.mutate(row.id);
                            }
                          }}
                        >
                          {copy.agentDetail.delete}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
